import type { ReactElement } from 'react';
import { CircularProgress } from '@mui/material';
import { RwOverflowMenu, type RwButtonSize, type RwButtonTier, type RwOverflowMenuItem } from '@/common/presentation/components/rw';

export interface LibraryOverflowAction {
  key: string;
  label: string;
  icon: ReactElement;
  onSelect: () => void;
  /** Destructive rows sit LAST behind the menu's one divider. */
  destructive?: boolean;
  disabled?: boolean;
  loading?: boolean;
  /** Swaps in for `label` while `loading` is true. Defaults to `label`. */
  loadingLabel?: string;
  testId?: string;
}

export interface LibraryOverflowMenuProps {
  actions: LibraryOverflowAction[];
  /** Accessible name for the ⋮ trigger — "Block actions for Letterhead". */
  label: string;
  testId?: string;
  size?: RwButtonSize;
  tier?: RwButtonTier;
}

function toMenuItem(action: LibraryOverflowAction): RwOverflowMenuItem {
  return {
    key: action.key,
    label: action.loading ? (action.loadingLabel ?? action.label) : action.label,
    icon: action.loading ? <CircularProgress size="var(--size-16)" aria-hidden /> : action.icon,
    onClick: action.onSelect,
    disabled: action.disabled || action.loading,
    destructive: action.destructive,
    'data-testid': action.testId
  };
}

/**
 * The card overflow — a thin adapter from the library kit's `LibraryOverflowAction` shape
 * (which supports a per-item async `loading` state) onto `RwOverflowMenu`'s `RwOverflowMenuItem`.
 * Holds only standalone commands that don't need the record open (Duplicate, Archive, Restore);
 * opening the record is the card title/open-link's job. Benign items render first, destructive
 * ones last — `RwOverflowMenu` draws its one divider above the first destructive row.
 */
export function LibraryOverflowMenu({ actions, label, testId, size, tier }: LibraryOverflowMenuProps) {
  const benign = actions.filter((action) => !action.destructive);
  const destructive = actions.filter((action) => action.destructive);
  const items = [...benign, ...destructive].map(toMenuItem);

  return <RwOverflowMenu items={items} size={size} tier={tier} aria-label={label} data-testid={testId} />;
}
